import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import { useMessagesStore } from '@lib/store';
import { useDebounce } from '@shared/hook';
import { cn, QueryUtils, RoutePaths } from '@shared/utils';
import { MessagesList, NewMessage } from './widgets';
import { Button, DatePicker, Input } from '@shared/widgets';
import BackIcon from '@assets/icons/back.svg';
import EditIcon from '@assets/icons/edit.svg';
import LoupeIcon from '@assets/icons/loupe.svg';

dayjs.extend(utc);
dayjs.extend(isBetween);

interface IDate {
  from: Date | null;
  to: Date | null;
}

export interface IFilter {
  searchQuery: string;
  dateFilter: IDate;
}

const DEFAULT_FILTER: IFilter = {
  searchQuery: '',
  dateFilter: { from: null, to: null },
};

const getFilterFromQueryParams = (): IFilter => {
  const query = new URLSearchParams(location.search);
  const from = query.get('from') ? dayjs.utc(query.get('from'), 'MM-DD-YYYY').toDate() : null;
  const to = query.get('to') ? dayjs.utc(query.get('to'), 'MM-DD-YYYY').toDate() : null;

  return { searchQuery: query.get('q') || '', dateFilter: { from, to } };
};

export const Messages = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const { data: messages, getMessages } = useMessagesStore();

  const [filter, setFilter] = useState<IFilter>(getFilterFromQueryParams());
  const [isShownNewMessage, setIsShownNewMessage] = useState<boolean>(false);
  const [isShownMobileSearch, setIsShownMobileSearch] = useState<boolean>(false);

  useEffect(() => {
    if (!params.dir) return;
    getMessages(params.dir === 'inbox' ? 'incomming' : 'outgoing');
  }, [params.dir]);

  const handleSearchChange = (searchQuery: string) => {
    setFilter((prev) => ({ ...prev, searchQuery }));
    const newQuery = new URLSearchParams(location.search);
    searchQuery ? newQuery.set('q', searchQuery) : newQuery.delete('q');
    navigate(`${location.pathname}?${newQuery.toString()}`);
  };
  const handleDateFilter = (dateFilter: IDate) => {
    setFilter((prev) => ({ ...prev, dateFilter }));
    const newQuery = new URLSearchParams(location.search);
    dateFilter.from ? newQuery.set('from', dayjs.utc(dateFilter.from).format('MM-DD-YYYY')) : newQuery.delete('from');
    dateFilter.to ? newQuery.set('to', dayjs.utc(dateFilter.to).format('MM-DD-YYYY')) : newQuery.delete('to');
    navigate(`${location.pathname}?${newQuery.toString()}`);
  };
  const clearSearchHandler = () => {
    setIsShownMobileSearch(false);
    setFilter(DEFAULT_FILTER);
    const newQuery = new URLSearchParams(location.search);
    newQuery.delete('q');
    navigate(`${location.pathname}?${newQuery.toString()}`);
  };

  const debouncedFilter = useDebounce(filter, 200);
  const filteredMessages = useMemo(() => {
    return Object.keys(filter).reduce((filtered, key) => {
      switch (key) {
        case 'dateFilter': {
          const { from, to } = filter.dateFilter;
          const startDate = dayjs.utc(from);
          const endDate = dayjs.utc(to);

          return startDate.isValid() || endDate.isValid()
            ? filtered.filter((message) => dayjs.utc(message.Date).isBetween(startDate, endDate, 'day', '[]'))
            : filtered;
        }
        case 'searchQuery': {
          const queryTest = QueryUtils.getSearchQuery(filter.searchQuery);
          return filtered.filter((message) => queryTest.test(message.Subject) || queryTest.test(message.Body));
        }
        default:
          return filtered;
      }
    }, messages);
  }, [debouncedFilter, messages]);

  return (
    <>
      <div className='fixed top-0 left-0 z-[1000] hidden h-[64px] w-full items-center justify-between bg-[linear-gradient(180deg,#282828,#212121)] shadow-[12px_12px_24px_0_#0000003b,inset_0_2px_0_0_#343434] max-md:flex'>
        <i className='flex h-full w-[64px] items-center justify-center' onClick={() => navigate(RoutePaths.dashboard.onNavigate())}>
          <BackIcon className='text-[#fff]' />
        </i>
        <h2 className='font-semibold text-[#fff] uppercase'>Messages</h2>
        <i
          className='flex h-full w-[64px] items-center justify-center bg-[linear-gradient(180deg,#a73f33,#a73f33)]'
          onClick={() => setIsShownNewMessage((prev) => !prev)}
        >
          <EditIcon className='text-[#fff]' />
        </i>
      </div>

      <div className={cn('page-animation', 'relative pt-5', 'max-md:p-0')}>
        <NewMessage isShown={isShownNewMessage} onClose={() => setIsShownNewMessage(false)} />
        <div className='mx-auto w-full max-w-[1294px] px-5 max-md:p-2.5'>
          <div className='relative mb-2.5 flex h-[42px] items-center justify-between pl-3 select-none max-md:hidden'>
            <div className='relative text-xl font-bold text-[#f2f2f2] uppercase before:absolute before:top-0 before:left-[-10px] before:font-semibold before:text-[#a73f33] before:content-["/"]'>
              Messages
            </div>
          </div>
          <div className='mb-6 flex items-center justify-between max-md:mb-2.5 max-md:block'>
            <div className='flex w-full items-center justify-between max-md:mb-2.5'>
              <div className='flex items-center justify-center max-md:order-2 max-md:mx-2.5 max-md:w-full'>
                <Link
                  className={cn(
                    'flex h-[52px] w-[130px] shrink-0 items-center justify-center rounded-l-sm bg-[linear-gradient(180deg,#525252,#494848)] text-center text-sm text-[#f2f2f2cc] uppercase no-underline shadow-[2px_2px_4px_0_#0000003b,inset_0_1px_0_0_#686868]',
                    'hover:bg-[linear-gradient(180deg,dimgray,#5b5b5b)]',
                    'max-md:w-1/2',
                    location.pathname.includes(RoutePaths.messages.onNavigate({ dir: 'inbox' })) &&
                      'bg-[linear-gradient(180deg,dimgray,#5b5b5b)] text-[#fff]',
                  )}
                  to={RoutePaths.messages.onNavigate({ dir: 'inbox' }, { queryString: location.search })}
                >
                  Inbox
                </Link>
                <Link
                  className={cn(
                    'flex h-[52px] w-[130px] shrink-0 items-center justify-center rounded-r-sm bg-[linear-gradient(180deg,#525252,#494848)] text-center text-sm text-[#f2f2f2cc] uppercase no-underline shadow-[2px_2px_4px_0_#0000003b,inset_0_1px_0_0_#686868]',
                    'hover:bg-[linear-gradient(180deg,dimgray,#5b5b5b)]',
                    'max-md:w-1/2',
                    location.pathname.includes(RoutePaths.messages.onNavigate({ dir: 'sent' })) &&
                      'bg-[linear-gradient(180deg,dimgray,#5b5b5b)] text-[#fff]',
                  )}
                  to={RoutePaths.messages.onNavigate({ dir: 'sent' }, { queryString: location.search })}
                >
                  Sent
                </Link>
              </div>
              <div className='mx-5 w-full max-md:order-1 max-md:m-0 max-md:w-[52px] max-md:min-w-[52px]'>
                <Button
                  className={cn(
                    '!hidden !h-[50px] w-[52px]',
                    'max-md:!flex',
                    isShownMobileSearch &&
                      '!bg-[linear-gradient(180deg,#e42d34,#a9161b)] !shadow-[12px_12px_24px_0_#0000003b,inset_0_2px_0_0_#fb454c]',
                  )}
                  onClick={() => setIsShownMobileSearch((prev) => !prev)}
                >
                  <LoupeIcon className={cn('fill-[#929292]', isShownMobileSearch && 'fill-[#fff]')} />
                </Button>
                <Input
                  className={cn('relative !h-[52px] !pr-10', 'max-md:hidden')}
                  variant='outlined'
                  rounded
                  type='search'
                  placeholder='SEARCH'
                  value={filter.searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className='max-md:order-3'>
              <DatePicker className='!h-[52px]' range={filter.dateFilter} onConfirm={handleDateFilter} />
            </div>
            <div className='min-w-[180px] pl-5 max-md:hidden'>
              <Button className='w-[160px]' onClick={() => setIsShownNewMessage((prev) => !prev)}>
                New message
              </Button>
            </div>
          </div>
          {isShownMobileSearch && (
            <div className='mb-5 flex items-center justify-center'>
              <Input variant='outlined' rounded placeholder='SEARCH' value={filter.searchQuery} onChange={handleSearchChange} />
              <div className='ml-2.5 flex items-center justify-center text-sm font-semibold text-[#97815e] uppercase' onClick={clearSearchHandler}>
                Cancel
              </div>
            </div>
          )}
          <div className='relative'>
            <MessagesList messages={filteredMessages} filter={filter} />
          </div>
        </div>
      </div>
    </>
  );
};
